import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ArrowRight, BookOpen } from 'lucide-react';
import servicesData from '../data/servicesData';
import ServiceModal from './ServiceModal';

const CALENDAR_URL = 'https://calendar.app.google/DtBNRYcoX9a3oD5a9';

/* ─── ServiceCard ─────────────────────────────────────── */
const ServiceCard = ({ svc, onOpen }) => (
  <div
    data-testid={`service-card-${svc.id}`}
    className="slide-in-card bg-white rounded-2xl p-5 border transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
    style={{ borderColor: '#F3F4F6', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}
  >
    {/* Subtitle badge */}
    <div className="flex items-center gap-2 mb-3">
      <span
        className="text-xs font-semibold px-2.5 py-1 rounded-full"
        style={{ backgroundColor: svc.bgColor, color: svc.iconColor }}
      >
        {svc.subtitle}
      </span>
    </div>

    {/* Clickable title */}
    <button
      data-testid={`service-title-${svc.id}`}
      onClick={() => onOpen(svc)}
      className="text-left w-full group mb-2"
    >
      <h3
        className="font-semibold text-base leading-tight transition-colors duration-200 group-hover:underline"
        style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
      >
        {svc.title}
      </h3>
    </button>

    {/* Summary — always visible */}
    <p className="text-sm mb-3 leading-relaxed" style={{ color: '#6B7280' }}>
      {svc.summary}
    </p>

    {/* Detail bullet points */}
    <ul className="space-y-1.5 mb-4">
      {svc.details.map((d) => (
        <li key={d} className="flex items-start gap-2 text-xs" style={{ color: '#4B5563' }}>
          <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0" style={{ color: svc.iconColor }} />
          {d}
        </li>
      ))}
    </ul>

    {/* "Read Full Guide" link */}
    <button
      data-testid={`service-read-more-${svc.id}`}
      onClick={() => onOpen(svc)}
      className="inline-flex items-center gap-1.5 text-xs font-semibold transition-opacity duration-200 hover:opacity-75"
      style={{ color: svc.iconColor }}
    >
      <BookOpen size={12} />
      Read Full Guide
      <ArrowRight size={11} />
    </button>
  </div>
);

/* ─── TimelineItem ────────────────────────────────────── */
const TimelineItem = ({ svc, isLeft, onOpen }) => {
  const itemRef = useRef(null);

  useEffect(() => {
    const card = itemRef.current?.querySelector('.slide-in-card');
    const dot  = itemRef.current?.querySelector('.pop-dot');
    if (!card) return;

    card.style.opacity   = '0';
    card.style.transform = `translateX(${isLeft ? '-32px' : '32px'})`;
    card.style.transition = 'opacity 0.7s ease, transform 0.7s ease';

    if (dot) {
      dot.style.opacity   = '0';
      dot.style.transform = 'scale(0.5)';
      dot.style.transition = 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34,1.56,0.64,1)';
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (dot) { dot.style.opacity = '1'; dot.style.transform = 'scale(1)'; }
          setTimeout(() => {
            card.style.opacity   = '1';
            card.style.transform = 'translateX(0)';
          }, 80);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );

    if (itemRef.current) io.observe(itemRef.current);
    return () => io.disconnect();
  }, [isLeft]);

  return (
    <div ref={itemRef} className="relative mb-8 md:mb-14">
      {/* Desktop layout */}
      <div className="hidden md:grid md:gap-0 items-start" style={{ gridTemplateColumns: '1fr 64px 1fr' }}>
        <div className="flex justify-end pr-8 pt-2">
          {isLeft && <div className="w-full max-w-sm ml-auto"><ServiceCard svc={svc} onOpen={onOpen} /></div>}
        </div>
        <div className="flex justify-center pt-2 pop-dot">
          <div
            className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center flex-shrink-0 relative z-10"
            style={{ border: `2px solid ${svc.iconColor}` }}
          >
            <svc.Icon size={18} style={{ color: svc.iconColor }} />
          </div>
        </div>
        <div className="pl-8 pt-2">
          {!isLeft && <div className="w-full max-w-sm"><ServiceCard svc={svc} onOpen={onOpen} /></div>}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="flex gap-4 items-start md:hidden pl-2">
        <div
          className="pop-dot flex-shrink-0 w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center z-10 mt-2"
          style={{ border: `2px solid ${svc.iconColor}` }}
        >
          <svc.Icon size={18} style={{ color: svc.iconColor }} />
        </div>
        <div className="flex-1">
          <ServiceCard svc={svc} onOpen={onOpen} />
        </div>
      </div>
    </div>
  );
};

/* ─── ServicesTimeline ────────────────────────────────── */
const ServicesTimeline = () => {
  const sectionRef = useRef(null);
  const lineRef    = useRef(null);
  const [selectedService, setSelectedService] = useState(null);

  /* Listen for footer service link clicks */
  useEffect(() => {
    const handler = (e) => {
      const svc = servicesData.find((s) => s.id === e.detail.serviceId);
      if (svc) setSelectedService(svc);
    };
    window.addEventListener('open-service', handler);
    return () => window.removeEventListener('open-service', handler);
  }, []);

  /* Animate the center line via IntersectionObserver */
  useEffect(() => {
    const line = lineRef.current;
    if (!line) return;

    line.style.transform  = 'scaleY(0)';
    line.style.transformOrigin = 'top center';
    line.style.transition = 'transform 1.8s ease';

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          line.style.transform = 'scaleY(1)';
          io.disconnect();
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <section
        ref={sectionRef}
        data-testid="services-section"
        id="services"
        aria-label="Our India-UK tax and advisory services"
        className="py-20 relative overflow-hidden"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        {/* Subtle dot-grid background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            opacity: 0.3,
          }}
        />
        <div className="max-w-6xl mx-auto px-6 relative">
          {/* Section header */}
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#2D5A8C' }}>
              What We Do
            </p>
            <h2
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
            >
              Our Services
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: '#6B7280' }}>
              Our specialized services tailored for India-UK cross-border tax and financial advisory. Click any service heading to explore the full guide.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Desktop center line */}
            <div
              ref={lineRef}
              className="absolute top-6 bottom-0 hidden md:block w-0.5"
              style={{ left: 'calc(50% - 0.5px)', backgroundColor: '#E5E7EB' }}
            />
            {/* Mobile left line */}
            <div
              className="absolute top-6 bottom-0 md:hidden w-0.5"
              style={{ left: '26px', backgroundColor: '#E5E7EB' }}
            />

            {servicesData.map((svc, i) => (
              <TimelineItem
                key={svc.id}
                svc={svc}
                isLeft={i % 2 === 0}
                onOpen={setSelectedService}
              />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-10">
            <a
              href={CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5"
              style={{ backgroundColor: '#F59E0B' }}
            >
              Schedule a Consultation
              <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>

      {/* Full-page overlay modal */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </>
  );
};

export default ServicesTimeline;
